import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "dropZone", "fileList", "error"];
  static values = {
    maxSize: { type: Number, default: 0 },
    accept: { type: String, default: "" },
    multiple: { type: Boolean, default: false },
    uploadingText: { type: String, default: "Uploading..." },
    uploadedText: { type: String, default: "Uploaded" },
    uploadErrorText: { type: String, default: "Upload error" },
    tooLargeText: { type: String, default: 'File "%{name}" exceeds maximum size of %{max} MB' },
    unsupportedTypeText: {
      type: String,
      default: 'File "%{name}" has unsupported type. Accepted: %{accept}',
    },
  };

  connect() {
    this.form = this.element.closest("form");
    if (!this.form) return;

    this._onDirectUploadStart = this._handleDirectUploadStart.bind(this);
    this._onDirectUploadProgress = this._handleDirectUploadProgress.bind(this);
    this._onDirectUploadError = this._handleDirectUploadError.bind(this);
    this._onDirectUploadEnd = this._handleDirectUploadEnd.bind(this);

    this.form.addEventListener("direct-upload:start", this._onDirectUploadStart);
    this.form.addEventListener("direct-upload:progress", this._onDirectUploadProgress);
    this.form.addEventListener("direct-upload:error", this._onDirectUploadError);
    this.form.addEventListener("direct-upload:end", this._onDirectUploadEnd);
  }

  disconnect() {
    if (!this.form) return;

    this.form.removeEventListener("direct-upload:start", this._onDirectUploadStart);
    this.form.removeEventListener("direct-upload:progress", this._onDirectUploadProgress);
    this.form.removeEventListener("direct-upload:error", this._onDirectUploadError);
    this.form.removeEventListener("direct-upload:end", this._onDirectUploadEnd);
  }

  openFilePicker(event) {
    event.preventDefault();
    this.inputTarget.click();
  }

  dragover(event) {
    event.preventDefault();
    this.dropZoneTarget.classList.add(
      "border-brand-500",
      "bg-brand-50/50"
    );
    this.dropZoneTarget.classList.remove("border-surface-border");
  }

  dragleave(event) {
    event.preventDefault();
    this.dropZoneTarget.classList.remove(
      "border-brand-500",
      "bg-brand-50/50"
    );
    this.dropZoneTarget.classList.add("border-surface-border");
  }

  drop(event) {
    event.preventDefault();
    this.dragleave(event);

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      this.inputTarget.files = files;
      this.handleFiles();
    }
  }

  handleFiles() {
    this.clearError();
    const files = Array.from(this.inputTarget.files);

    if (files.length === 0) return;

    for (const file of files) {
      if (!this.validateFile(file)) return;
    }

    this.displayFiles(files);
  }

  validateFile(file) {
    if (this.maxSizeValue > 0 && file.size > this.maxSizeValue) {
      const maxMb = (this.maxSizeValue / (1024 * 1024)).toFixed(1);
      this.showError(
        this.tooLargeTextValue.replace("%{name}", file.name).replace("%{max}", maxMb)
      );
      this.inputTarget.value = "";
      return false;
    }

    if (this.acceptValue) {
      const acceptedTypes = this.acceptValue.split(",").map((t) => t.trim());
      const isAccepted = acceptedTypes.some((type) => {
        if (type.startsWith(".")) {
          return file.name.toLowerCase().endsWith(type.toLowerCase());
        }
        if (type.endsWith("/*")) {
          return file.type.startsWith(type.replace("/*", "/"));
        }
        return file.type === type;
      });

      if (!isAccepted) {
        this.showError(
          this.unsupportedTypeTextValue
            .replace("%{name}", file.name)
            .replace("%{accept}", this.acceptValue)
        );
        this.inputTarget.value = "";
        return false;
      }
    }

    return true;
  }

  displayFiles(files) {
    this.fileListTarget.innerHTML = "";
    this.fileListTarget.classList.remove("hidden");

    for (const file of files) {
      const item = document.createElement("div");
      item.className =
        "flex items-center justify-between p-2 bg-surface rounded-ui border border-surface-border";
      item.dataset.fileName = file.name;

      const info = document.createElement("div");
      info.className = "flex items-center gap-2 text-sm text-content-secondary min-w-0 flex-1";

      const name = document.createElement("span");
      name.className = "truncate";
      name.textContent = file.name;

      const size = document.createElement("span");
      size.className = "text-content-muted flex-shrink-0";
      size.textContent = this.formatSize(file.size);

      info.appendChild(name);
      info.appendChild(size);

      // Progress bar container (hidden until direct upload starts)
      const progressContainer = document.createElement("div");
      progressContainer.className = "hidden w-24 flex-shrink-0";
      progressContainer.dataset.role = "progress";

      const progressTrack = document.createElement("div");
      progressTrack.className = "h-1.5 bg-surface-subtle rounded-ui-full overflow-hidden";

      const progressBar = document.createElement("div");
      progressBar.className = "h-full bg-brand-500 rounded-ui-full transition-all duration-150";
      progressBar.style.width = "0%";
      progressBar.dataset.role = "progressBar";

      progressTrack.appendChild(progressBar);
      progressContainer.appendChild(progressTrack);

      // Status text (shown after upload completes or errors)
      const status = document.createElement("span");
      status.className = "text-xs flex-shrink-0 hidden";
      status.dataset.role = "status";

      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className =
        "text-content-muted hover:text-error transition-colors flex-shrink-0 p-1";
      removeBtn.innerHTML =
        '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>';
      removeBtn.dataset.role = "removeBtn";
      removeBtn.addEventListener("click", () => {
        this.inputTarget.value = "";
        this.fileListTarget.classList.add("hidden");
        this.fileListTarget.innerHTML = "";
      });

      item.appendChild(info);
      item.appendChild(progressContainer);
      item.appendChild(status);
      item.appendChild(removeBtn);
      this.fileListTarget.appendChild(item);
    }
  }

  showError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("hidden");
  }

  clearError() {
    this.errorTarget.textContent = "";
    this.errorTarget.classList.add("hidden");
  }

  formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // Direct Upload event handlers

  _handleDirectUploadStart(event) {
    const { id } = event.detail;
    const item = this._findFileItem(id);
    if (item) {
      const progress = item.querySelector('[data-role="progress"]');
      const removeBtn = item.querySelector('[data-role="removeBtn"]');
      if (progress) progress.classList.remove("hidden");
      if (removeBtn) removeBtn.classList.add("hidden");
    }
    this._setSubmitButtonsDisabled(true);
  }

  _handleDirectUploadProgress(event) {
    const { id, progress } = event.detail;
    const item = this._findFileItem(id);
    if (item) {
      const bar = item.querySelector('[data-role="progressBar"]');
      if (bar) bar.style.width = `${progress}%`;
    }
  }

  _handleDirectUploadError(event) {
    event.preventDefault();
    const { id } = event.detail;
    const item = this._findFileItem(id);
    if (item) {
      const progressContainer = item.querySelector('[data-role="progress"]');
      const status = item.querySelector('[data-role="status"]');
      if (progressContainer) progressContainer.classList.add("hidden");
      if (status) {
        status.textContent = this.uploadErrorTextValue;
        status.className = "text-xs flex-shrink-0 text-error";
        status.classList.remove("hidden");
      }
    }
    this.showError(event.detail.error);
    this._setSubmitButtonsDisabled(false);
  }

  _handleDirectUploadEnd(event) {
    const { id } = event.detail;
    const item = this._findFileItem(id);
    if (item) {
      const progressContainer = item.querySelector('[data-role="progress"]');
      const status = item.querySelector('[data-role="status"]');
      if (progressContainer) progressContainer.classList.add("hidden");
      if (status) {
        status.textContent = this.uploadedTextValue;
        status.className = "text-xs flex-shrink-0 text-success-dark";
        status.classList.remove("hidden");
      }
    }
    this._setSubmitButtonsDisabled(false);
  }

  _findFileItem(uploadId) {
    // Direct Upload assigns numeric IDs; match by order in file list
    const items = this.fileListTarget.querySelectorAll("[data-file-name]");
    return items[uploadId] || null;
  }

  _setSubmitButtonsDisabled(disabled) {
    if (!this.form) return;
    this.form.querySelectorAll('[type="submit"]').forEach((btn) => {
      btn.disabled = disabled;
    });
  }
}
